
import { Component, Vue, Watch } from 'vue-property-decorator'
import moment, { Moment } from 'moment'

import config from '@/config'

import DateSelector from '@/components/generic/DateSelector.vue'
import TemplateSelector from '@/components/generic/TemplateSelector.vue'
import UserItemInfoIcons from '@/components/tracking/UserItemInfoIcons.vue'
import UsersLocationMap from '@/components/maps/UsersLocationMap.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import UserGroupItem from '@/components/tracking/UserGroupItem.vue'
import TasksUsersLocationMap from '@/components/maps/TasksUsersLocationMap.vue'

import authService from '@/services/support/AuthService'
import trackingService from '@/services/TrackingService'
import firebaseService from '@/services/FirebaseService'
import trackingPositionsVersionsHandler from '@/services/TrackingPositionsVersionsHandler'
import templatesService from '@/services/TemplatesService'
import cookiesService from '@/services/support/CookiesService'
import LogService from '@/services/support/LogService'

import { PageRequest, PageResponse, CursorRequest, SortDirection } from '@/types/Support'
import { Template } from '@/types/Templates'
import { TrackingUserGroup, TrackingUser, TrackingUserPosition, TrackingUserTaskLocationStatus, TrackingUserTask, TrackingUserStatus, TrackingUserInfotmation, TrackingUserTasksFilters, TrackingUserActivityType, PageRequestTrackingFilters, PageRequestTrackingUserSession, LocationStatusFills, TrackingUserSession } from '@/types/Tracking'
import { FirebaseRefListener, FirebaseEvents } from '@/types/Firebase'
import FillgridService from '@/services/FillgridService'
import CatalogsService from '@/services/CatalogsService'
import { DateUtils } from '@/services/DateFormat'
import { Role, RoleCatalogFilters } from '@/types/Catalogs'
import { GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'
import userInfoStore from '@/store/userInfo'

@Component({
  components: {
    DateSelector,
    LoadingMask,
    TemplateSelector,
    UsersLocationMap,
    TasksUsersLocationMap,
    UserItemInfoIcons,
    UserGroupItem
  }
})
export default class MainLayout extends Vue {
  // Constants
  ACTIVITY_TYPE = TrackingUserActivityType
  MINUTES_TO_CONSIDER_USER_INACTIVE = 30
  MAX_USERS_PAGE_SIZE = 500
  MAX_TASKS_PAGE_SIZE = 150
  MAX_TASKS_PAGE_SIZE_FILLS = 100
  INTERVAL_TO_CHECK_CONNECTION_STATUS = 60 * 1000; // Cada minuto
  INTERVAL_TO_CHECK_POSITION_CHANGES = 3 * 1000 // Cada 5 segundos
  ITEMS_TRANSITION_DELAY = 70
  ITEMS_PAGE = 4
  MAP_VIEW = {
    USERS: 1,
    TASKS_AND_PATH: 2
  }
  VIEW_ERRORS = {
    MISSING_DOMAIN: 1,
    MISSING_PROJECT: 2,
    MISSING_USER: 3,
    FIREBASE_AUTH_FAILED: 4
  }
  DATE_31_DAYS_BEFORE = moment().add(-31, 'day').toDate()

  // Status enums
  USER_STATUS = TrackingUserStatus
  TASK_STATUS = TrackingUserTaskLocationStatus

  // View state
  ready = false
  sidebarCollapsed = false
  currentMapView = this.MAP_VIEW.USERS
  validError = 0

  // Templates
  historyTemplates: Template[] = []
  templateSelected: number = 0
  templateIdents: any[] = []
  missingTemplate: boolean = false

  // Groups
  userGroups: TrackingUserGroup[] = []
  selectedGroup: TrackingUserGroup = new TrackingUserGroup()

  // Users
  loadingUsersState = 0
  progressLoadingUsers = 0
  allUsers: TrackingUser[] = []
  userSelected = new TrackingUser()
  usersNames: string[] = []
  userSearch = ''
  usersAutoZoom = true
  loadingSearch = false
  searchPerformed = false
  usersOnlineDiabled = false
  usersOfflineDiabled = true
  usersInactiveDiabled = true
  pageRequestOnlineUsers = new PageRequest(this.ITEMS_PAGE)
  pageRequestOfflineUsers = new PageRequest(this.ITEMS_PAGE)
  pageRequestInactiveUsers = new PageRequest(this.ITEMS_PAGE)
  onlineUsers = new PageResponse<any>()
  offlineUsers = new PageResponse<any>()
  inactiveUsers = new PageResponse<any>()
  onlineUsersTotal = 0
  offlineUsersTotal = 0
  inactiveUsersTotal = 0
  listeners: FirebaseRefListener[] = []
  loadedUserGroups: any = {}
  loadedUsersIds: number[]

  // User updates
  usersToUpdate: TrackingUser[] = []
  usersIdsToUpdate: number[] = []
  lastIndexUserUpdated: number

  // Tasks
  pageRequestUserTasksWorked = new PageRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE)
  pageRequestUserTasksPending = new PageRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE)
  cursorRequestUserTasksWorked = new CursorRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE_FILLS, true)
  cursorRequestUserTasksPending = new CursorRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE_FILLS, true)
  userTasks: TrackingUserTask[] = []
  userTasksWorked: TrackingUserTask[] = []
  userTasksPending: TrackingUserTask[] = []
  totalUserTasksWorked = 0
  totalUserTasksPending = 0
  loadingUserTasksWorked = false
  loadingUserTasksPending = false
  completedTasks: any[] = []
  uncompletedTasks: any[] = []
  completedWarnTasks: any[] = []
  completedTasksValid: any[] = []
  uncompletedTasksValid: any[] = []
  completedWarnTasksValid: any[] = []
  completedTasksDiabled = false
  uncompletedTasksDiabled = true
  completedWarnTasksDiabled = false
  taskSelected: any = null
  tasksVisibleOnMap = false

  // Paths
  pathVisibleOnMap = false
  pathUserId = 0
  snappedPathActive = false
  pathCount = 0
  loadingPath = false

  // Paths Count
  pathCountLabels: any[] = []
  totalKilometers = 0
  kilometersEstimated = false
  pathCountVisible = false
  loadingPathCount = true

  // Path user info
  dateSelected = new Date()
  firstUserConnection = ''
  lastUserConnection = ''

  // UserSessions
  userSessionsLoaded = false
  userSessions = new PageResponse<TrackingUserSession>()

  refreshStatusInterval: any = ''
  refreshPositionsInterval: any = ''
  tasksPathPriority = 'tasks' // values: [tasks, path]

  async created () {
    if (this.$store.state.global.mapLayoutSidebar) {
      this.toggleMainMenuSidebar()
    }

    this.init()
      .then(() => {
        this.loadGroups()
      })
  }

  mounted () {
    this.$root.$on('EVENT_TRACKING_SET_PATH_COUNT', this.setPathCountValues)
  }

  beforeDestroy () {
    this.disposeMainUserRefListeners()
    this.$root.$off('EVENT_TRACKING_SET_PATH_COUNT', this.setPathCountValues)
    clearInterval(this.refreshStatusInterval)
    clearInterval(this.refreshPositionsInterval)
  }

  /**
   * Init tracker data
   */
  async init () {
    if (!this.validView()) return

    // Firebase auth
    try {
      const user = await firebaseService.auth()
      if (user) this.ready = true
    } catch (error) {
      this.validError = this.VIEW_ERRORS.FIREBASE_AUTH_FAILED
    }
  }

  /**
   * Invoke the init() method if neccesary cookies are present
   */
  refresh () {
    this.validError = 0
    setTimeout(() => {
      this.init()
    }, 500)
  }

  /**
   * Validate the view's preconditions
   */
  async validView () {
    if (!cookiesService.get(config.domainUIDCookieName)) {
      this.validError = this.VIEW_ERRORS.MISSING_DOMAIN
      return false
    }

    if (!cookiesService.get(config.projectIdCookieName)) {
      this.validError = this.VIEW_ERRORS.MISSING_PROJECT
      return false
    }

    if (!cookiesService.get(config.jwtAccessTokenCookieName)) {
      if (cookiesService.get(config.refreshTokenCookieName)) {
        await authService.refreshToken(cookiesService.get(config.refreshTokenCookieName))
      } else {
        this.validError = this.VIEW_ERRORS.MISSING_USER
        return false
      }
    }

    return true
  }

  /**
   * Status that should appear in the map
   */
  get usersStatusSelected () {
    return [
      this.usersOnlineDiabled ? -1 : TrackingUserStatus.ONLINE,
      this.usersInactiveDiabled ? -1 : TrackingUserStatus.INACTIVE,
      this.usersOfflineDiabled ? -1 : TrackingUserStatus.OFFLINE
    ]
  }

  /**
   * Tasks status that must be appear in the map
   */
  get tasksStatusToShow () {
    let statusArray: number[] = []
    if (!this.completedTasksDiabled) statusArray.push(TrackingUserTaskLocationStatus.COMPLETE)
    if (!this.completedWarnTasksDiabled) statusArray.push(TrackingUserTaskLocationStatus.COMPLETE_WARN)
    if (!this.uncompletedTasksDiabled) statusArray.push(TrackingUserTaskLocationStatus.UNCOMPLETE)
    return statusArray
  }

  /**
   * Get the search input to filter users
   */
  get searchInputText () : any {
    return this.$refs.searchInput
  }

  /**
   * Calculates the total time that a user was connected along the day
   */
  get totalConnectionTime () {
    if (this.firstUserConnection && this.lastUserConnection) {
      return moment.utc(moment(this.lastUserConnection, 'HH:mm:ss').diff(moment(this.firstUserConnection, 'HH:mm:ss'))).format('HH:mm:ss')
    } else {
      return ''
    }
  }

  /**
   * Indicates that a user group is selected
   */
  get isGroupSelected () {
    return this.selectedGroup.id || false
  }

  /**
   * When the selected date changes, update the tasks markers and/or path markers
   */
  @Watch('dateSelected')
  watchDateSelected () {
    this.resetUserTasks()
    if (this.tasksVisibleOnMap) this.loadUserTasks()
    if (this.pathVisibleOnMap) this.loadUserPath()
  }

  /**
   * When the selected date changes, update the tasks markers and/or path markers
   */
  @Watch('userSelected')
  watchUserSelected () {
    this.pathUserId = 0
    this.snappedPathActive = false
    this.hidePathCount()
  }

  /**
   * Clean firebase listeners for last user positions updates
   */
  disposeMainUserRefListeners () {
    this.listeners.forEach(it => {
      it.ref.off(it.event, it.listener)
    })
  }

  /**
   * Show / Collapse tracking sidebar (useful for mobile)
   */
  toggleSidebarCollapsed () {
    this.sidebarCollapsed = !this.sidebarCollapsed
  }

  /**
   * Show main menu sidebar
   */
  toggleMainMenuSidebar () {
    this.$store.commit('global/toggleMapLayoutSidebar')
  }

  /************************************************************************************************/
  /* TEMPLATES ************************************************************************************/
  /************************************************************************************************/

  /**
   * Gets the idents from the template selected
   * This could be at least 1 and max 3.
   *
   * @param templateId to get idents
   */
  async loadIdentsFromTemplate (templateId: number) {
    await FillgridService.getTemplateById(templateId)
      .then((res) => {
        let identLabels = res.idents || []
        let questions = res.questions || []
        this.getIdents(identLabels, questions)
      })
  }

  /**
   * Gets the name and label question values by idents
   * from a template questions
   *
   * @param idents ids
   * @param questions from template
   */
  getIdents (idents: any[], questions: any[]) {
    let queueQuestions: any[] = questions
    while (queueQuestions.length > 0) {
      // when get all the idents break the loop
      if (idents.length === this.templateIdents.length) { break }
      let question = queueQuestions.shift()
      if (question.hasOwnProperty('id') && idents.includes(question.id)) {
        this.templateIdents.push(
          { 'name': question.id, 'label': question.label }
        )
      } else if (question.hasOwnProperty('questions') &&
        question.questions.length > 0) {
        queueQuestions = queueQuestions.concat(question.questions)
      }
    }
  }

  /**
   * Gets the latest templates used in this browser
   */
  loadHistoryTemplates (templates: Template[]) {
    this.historyTemplates = templatesService.getLatestSelectedTemplatesForTracking()
    const templatesIds = templates.map(it => it.id, [])
    this.historyTemplates = this.historyTemplates.filter(it => templatesIds.includes(it.id))
  }

  /**
   * On history template selection
   */
  selectTemplateFromHistory (template: Template) {
    this.templateSelected = template.id
  }

  /**
   * On template selection
   */
  selectTemplate (template: Template) {
    this.missingTemplate = false
    this.templateSelected = template.id
    templatesService.addItemToLatestSelectedTemplatesForTracking(template)
    this.loadIdentsFromTemplate(template.id)
    if (this.userGroups.length) {
      this.returnToGroupList(false, false)
    } else {
      this.loadGroups()
    }
    // Hide the tasks and path if a users is already selected
    if (this.userSelected.id) {
      this.tasksVisibleOnMap = true
      this.pathVisibleOnMap = true
      this.toggleUserTasks()
      this.toggleUserPath()
    }
  }

  /************************************************************************************************/
  /* USER GROUPS **********************************************************************************/
  /************************************************************************************************/

  /**
   * @returns a PageRequest with the configurations to request the groups
   */
  preparePageGroupRequest () : PageRequest<GroupCatalogFilters> {
    let pageRequest = new PageRequest<GroupCatalogFilters>()
    let domainId = cookiesService.get(config.domainIdCookieName)
    pageRequest.sort = 'name'
    if (userInfoStore.inRootDomain()) {
      pageRequest.filters.domain = domainId
    }
    pageRequest.filters.status = GroupStatus.ACTIVE
    return pageRequest
  }

  /**
   * Gets users groups and its subgroups
   */
  async loadGroups () {
    let pageRequest = this.preparePageGroupRequest()
    await CatalogsService.getGroups(pageRequest)
      .then((response) => {
        this.userGroups = response.results as TrackingUserGroup[]
      })
  }

  /**
   * Select a group in order to load users that it contains
   */
  selectGroup (group: TrackingUserGroup) {
    this.selectedGroup = group
    this.loadUsers()
  }

  /**
   * Resets the maps and show the whole list of groups
   */
  returnToGroupList (clearGroup: boolean, clearUsers: boolean) {
    this.changeLoadingUsersState(2)
    if (clearGroup) {
      this.selectedGroup = new TrackingUserGroup()
    }
    setTimeout(() => {
      if (clearUsers) {
        this.cleanLoadedUsers()
      }
      this.changeLoadingUsersState(0)
    }, 500)
  }

  /************************************************************************************************/
  /* USERS ****************************************************************************************/
  /************************************************************************************************/

  /**
   * Gets users from api
   */
  async loadUsers () {
    LogService.log('********************************************************************')
    LogService.log(`** ${moment().format('hh:mm:ss')} - Inicia carga`)

    try {
      this.changeLoadingUsersState(1)
      if (this.loadedUserGroups.hasOwnProperty(this.selectedGroup.id)) {
        this.progressLoadingUsers = 90

        setTimeout(() => {
          LogService.log(`** Usuarios en cache - cargando desde la memoria`)
          this.allUsers = this.loadUsersFromLoadedGroups(this.selectedGroup)

          this.allUsers.forEach((it, index) => {
            if (it._lastPositionKey) {
              this.addUserToPendingChanges(it)
            }
          })
          this.commitUserPositionChanges()

          this.loadListeners(this.allUsers)
          this.startIntervalsToApplyChanges()
          this.changeLoadingUsersState(0)
          this.initUsersByStatus(true)
        }, 500)
      } else {
        this.progressLoadingUsers = 0

        let pageRequestUsers = new PageRequest<PageRequestTrackingFilters>(this.MAX_USERS_PAGE_SIZE)
        pageRequestUsers.filters.onlyActiveUsers = true
        pageRequestUsers.filters.onlyMobileUsers = true
        // Add the date range to get users from the current day
        pageRequestUsers.filters.from = moment().set({ hour: 0, minute: 0, second: 0 }).toDate().getTime()
        pageRequestUsers.filters.to = moment().set({ hour: 23, minute: 59, second: 59 }).toDate().getTime()

        let trackerRoles = await CatalogsService
          .getRoles(new PageRequest<RoleCatalogFilters>(this.MAX_USERS_PAGE_SIZE))
          .then((roles: PageResponse<Role>) => {
            let roleNames = ['mobile', 'typist', 'web']
            let trackerRoles: Role[] = roles.results
              .filter((role: Role) =>
                role.domain === -1 && roleNames.includes(role.name)
              )
            return trackerRoles
          })

        let loadingUsers = true
        while (loadingUsers) {
          if (!this.isGroupSelected) throw new Error('Carga de usuarios cancelada')

          let pageResponse = await trackingService
            .getPagedUsersByGroupAPIUSERS(
              this.selectedGroup.id,
              trackerRoles,
              pageRequestUsers
            )

          if (!this.isGroupSelected) throw new Error('Carga de usuarios cancelada')

          this.allUsers = this.allUsers.concat(pageResponse.content)
          loadingUsers = this.allUsers.length < pageResponse.totalElements
          this.progressLoadingUsers = Math.ceil(this.allUsers.length / pageResponse.totalElements * 100)

          // Init events in order to listen changes about users
          this.loadListeners(pageResponse.content)
          if (pageRequestUsers.page === 0) {
            this.startIntervalsToApplyChanges()
          }

          pageRequestUsers.page++
        }
        this.changeLoadingUsersState(0)
        this.addUsersToLoadedGroups(this.selectedGroup, this.allUsers)
      }

      this.initUserNames(this.allUsers)
    } catch (error) {
      LogService.error(error)
    } finally {
      this.initUsersByStatus(true)
      LogService.log(`** ${moment().format('hh:mm:ss')} - Finaliza carga`)
    }
  }

  /**
   * Start listeners in order to get events with latest positions and user info
   */
  loadListeners (users: TrackingUser[]) {
    const domainUID = cookiesService.get(config.domainUIDCookieName)
    const startOfDay = moment().startOf('day').utc().valueOf().toString()

    for (let it of users) {
      /** User position event */
      const key = it._lastPositionKey ? (it._lastPositionKey + 1).toString() : startOfDay

      // Initializes user devices info
      let initialInfo = firebaseService.getUserLastRecordedPositionRef(domainUID, it.id).orderByKey()
      initialInfo.once(FirebaseEvents.VALUE, (snapshot: any) => {
        let lastRecordedPosition = snapshot.val()
        if (snapshot.val() !== null) {
          let lastPositions = Object.values(lastRecordedPosition)
          if (lastPositions.length > 0) {
            this.updateUserPosition(it, lastPositions[0] as TrackingUserPosition)
          }
        }
      })

      let positionRef = firebaseService.getUserLastRecordedPositionRef(domainUID, it.id).orderByKey().startAt(key)

      const addedPositionListener = positionRef.on(FirebaseEvents.CHILD_ADDED, (snapshot: any) => {
        it._lastPositionKey = snapshot.key
        this.updateUserPosition(it, snapshot.val())
      })

      this.listeners.push(new FirebaseRefListener(positionRef, FirebaseEvents.CHILD_ADDED, addedPositionListener))

      /** User information event */
      let userInfoRef = firebaseService.getUserInfoRef(domainUID, it.id)

      const userInfoListener = userInfoRef.on(FirebaseEvents.CHILD_CHANGED, (snapshot: any) => {
        this.updateUserInfo(it, snapshot.val())
        // console.log('******* Updated info ***************')
      })

      this.listeners.push(new FirebaseRefListener(userInfoRef, FirebaseEvents.CHILD_CHANGED, userInfoListener))
    }
  }

  startIntervalsToApplyChanges () {
    // Start interval that checks the status of the users acording to their last connection time
    this.refreshStatusInterval = setInterval(() => {
      this.refreshUsersStatus()
    }, this.INTERVAL_TO_CHECK_CONNECTION_STATUS)

    // Start interval that checks the status of the users acording to their last connection time
    this.refreshPositionsInterval = setInterval(() => {
      this.commitUserPositionChanges()
    }, this.INTERVAL_TO_CHECK_POSITION_CHANGES)
  }

  /**
   * Update the user position data based on the firebase events
   */
  updateUserPosition (user: TrackingUser, data: TrackingUserPosition) {
    try {
      // Update user path if it's loaded on map
      // and only if the dateSelected is the same as today
      if (this.pathUserId === user.id && DateUtils.areDatesSameDay(new Date(), this.dateSelected)) {
        this.processUserPath(data)
      }

      data = trackingPositionsVersionsHandler.getValidPositionObject(data)

      if (user._lastPositionKey) {
        // Backup the current user status
        const currentStatus = user.status
        const currentPosition = user.position.position
        const currentMockGps = user.mockGps

        this.setUserData(user, data)

        if (currentStatus !== user.status || currentPosition !== user.position.position || currentMockGps !== user.mockGps) {
          this.addUserToPendingChanges(user)
        }
      } else {
        this.setUserData(user, data)
        this.addUserToPendingChanges(user)
      }
    } catch (error) {
      LogService.error(error)
    }
  }

  /**
   * Update the user information based on the firebase events
   */
  updateUserInfo (user: TrackingUser, data: TrackingUserInfotmation) {
    if (data && (data.firstName || user.lastName)) {
      user.firstName = data.firstName
      user.lastName = data.lastName
      this.addUserToPendingChanges(user)
    }
  }

  /**
   * Add user to the pending changes list
   */
  addUserToPendingChanges (user: TrackingUser) {
    this.usersToUpdate.push(user)
    this.usersIdsToUpdate.push(user.id)
  }

  /**
   * Send changues to the map from the pending changes list
   */
  commitUserPositionChanges () {
    if (this.lastIndexUserUpdated === this.usersToUpdate.length) return

    this.usersToUpdate = this.usersToUpdate.slice(this.lastIndexUserUpdated)
    this.usersIdsToUpdate = this.usersIdsToUpdate.slice(this.lastIndexUserUpdated)

    this.lastIndexUserUpdated = this.usersToUpdate.length

    this.$root.$emit('EVENT_TRACKING_APPLY_POSITIONS_CHANGES', this.usersToUpdate)
  }

  /**
   * Event that is fired when map notifies that changes have been applied
   */
  cleanUsersFromPendingChanges () {
    this.refreshUsersBySatus()

    if (this.currentMapView === this.MAP_VIEW.USERS) {
      if (this.userSelected.id) {
        if (this.usersIdsToUpdate.indexOf(this.userSelected.id) > -1) {
          setTimeout(() => {
            this.zoomInSelectedUser()
          }, 300)
        }
      } else if (this.usersAutoZoom) {
        setTimeout(() => {
          this.$root.$emit('EVENT_TRACKING_ZOOM_ALLUSERS')
        }, 500)
      }
    }
  }

  /**
   * Sets / Udpates the user's data
   */
  setUserData (user: TrackingUser, data: TrackingUserPosition) {
    user.status = this.getUserStatus(data._timestamp)

    // Update other fields
    user._lastTimeConnected = data._timestamp
    user.device.device = data.device
    user.device.uuid = data.uuid || user.device.uuid || data.imei
    user.position.position = data.position
    user.mockGps = data.mock
    user.batteryIsCharging = data.batteryIsCharging
    user.batteryLevel = data.batteryLevel
    user.activityType = data.activityType
    user.activityConfidence = data.activityConfidence
  }

  refreshUsersBySatus () {
    this.loadOnlineUsersPage()
    this.loadOfflineUsersPage()
    this.loadInactivelineUsersPage()
  }

  initUserNames (users: TrackingUser[]) {
    this.usersNames = []
    for (let i = 0; i < users.length; i++) {
      this.usersNames.push(users[i].name)
    }
  }

  /**
   * Adds users recusively to the loadedUserGroups object
   */
  addUsersToLoadedGroups (group: TrackingUserGroup, users: TrackingUser[]) {
    return new Promise((resolve, reject) => {
      try {
        this.loadedUserGroups[group.id] = users.filter(usr => usr.groups.find(itg => itg.id === group.id) !== undefined)
        if (group.subGroups && group.subGroups.length) {
          for (let it of group.subGroups) {
            this.addUsersToLoadedGroups(it, users)
          }
        }
        resolve(true)
      } catch (error) {
        LogService.error(error)
        reject(error)
      }
    })
  }

  /**
   * Gets users preloaded recusively
   */
  loadUsersFromLoadedGroups (group: TrackingUserGroup): TrackingUser[] {
    let arr: TrackingUser[] = [].concat(this.loadedUserGroups[group.id].filter((it: TrackingUser) => this.loadedUsersIds.indexOf(it.id) === -1))
    let ids = arr.reduce<number[]>((arr, it) => arr.concat(Number(it.id)), [])
    this.loadedUsersIds = this.loadedUsersIds.concat(ids)
    if (group.subGroups && group.subGroups.length) {
      for (let it of group.subGroups) {
        arr = arr.concat(this.loadUsersFromLoadedGroups(it))
      }
    }
    return arr
  }

  /**
   * Clean selected user and reset related data
   */
  cleanSelectedUser () {
    this.missingTemplate = false
    this.userSelected = new TrackingUser()
    this.dateSelected = new Date()
    this.currentMapView = this.MAP_VIEW.USERS
    this.tasksVisibleOnMap = false
    this.pathVisibleOnMap = false
    this.userSessions = new PageResponse<TrackingUserSession>()
    this.userSessionsLoaded = false
    this.resetUserTasks()
    this.$root.$emit('EVENT_TRACKING_ZOOM_ALLUSERS')
  }

  /**
   * Resets the loaded users and dispose all data and events related to them
   */
  cleanLoadedUsers () {
    clearInterval(this.refreshStatusInterval)
    clearInterval(this.refreshPositionsInterval)

    this.disposeMainUserRefListeners()
    this.cleanSelectedUser()

    this.userSearch = ''
    this.searchPerformed = false

    this.allUsers = []
    this.usersNames = []

    this.loadedUsersIds = []
    this.usersToUpdate = []
    this.usersIdsToUpdate = []
    this.lastIndexUserUpdated = 0

    this.$root.$emit('EVENT_TRACKING_CLEAN_MAP')
    this.initUsersByStatus(true)
  }

  changeLoadingUsersState (state: number) {
    this.loadingUsersState = state
  }

  /**
   * Select a user from the list and show it in the map
   */
  selectUser (user: TrackingUser) {
    this.userSelected = user
    if (user.position.position) {
      setTimeout(() => {
        this.zoomInSelectedUser()
      }, 750)
    }
  }

  /**
   * Event fired when a user is clicked in the map
   */
  selectUserFromMap (username: string) {
    const user = this.allUsers.find(it => it.name === username)
    if (user) this.selectUser(user)
  }

  /**
   * Check the last time that the online users were connected
   * in order to refresh the status from online to inactive
   */
  refreshUsersStatus () {
    for (let user of this.allUsers) {
      if (user.status === TrackingUserStatus.OFFLINE) continue

      const prevStatus = user.status
      const newStatus = this.getUserStatus(user._lastTimeConnected)

      if (prevStatus !== newStatus) {
        user.status = newStatus
        this.addUserToPendingChanges(user)
      }
    }
  }

  /**
   * Determines the status of the user based on the last time connected
   */
  getUserStatus (lastTimeConnectedUTC: Moment) {
    const currentTime = moment()
    const lastTimeConnection = lastTimeConnectedUTC.local()
    const isToday = lastTimeConnection.isSame(currentTime, 'd')

    if (isToday) { // If is today
      const ms = currentTime.diff(lastTimeConnection)
      const minutes = moment.duration(ms).asMinutes()

      if (minutes >= this.MINUTES_TO_CONSIDER_USER_INACTIVE) {
        return TrackingUserStatus.INACTIVE
      } else {
        return TrackingUserStatus.ONLINE
      }
    } else { // If is not today
      return TrackingUserStatus.OFFLINE
    }
  }

  /**
   * Create the current page for the online users list
   */
  loadOnlineUsersPage (init? : boolean) {
    if (init) {
      this.pageRequestOnlineUsers = new PageRequest(this.ITEMS_PAGE)
      this.onlineUsers = new PageResponse<any>()
    }

    let filteredUsers = this.allUsers.filter(it => it.status === TrackingUserStatus.ONLINE)
    this.onlineUsersTotal = filteredUsers.length
    if (this.userSearch.length) {
      const regex = new RegExp(this.userSearch, 'i')
      filteredUsers = filteredUsers.filter(it => regex.test(it.name))
    }

    const end = this.pageRequestOnlineUsers.page * this.pageRequestOnlineUsers.size + this.pageRequestOnlineUsers.size
    this.onlineUsers.content = filteredUsers.slice(0, end)
    this.onlineUsers.totalElements = filteredUsers.length
  }

  /**
   * Create the current page for the offline users list
   */
  loadOfflineUsersPage (init? : boolean) {
    if (init) {
      this.pageRequestOfflineUsers = new PageRequest(this.ITEMS_PAGE)
      this.offlineUsers = new PageResponse<any>()
    }

    let filteredUsers = this.allUsers.filter(it => it.status === undefined || it.status === TrackingUserStatus.OFFLINE)
    this.offlineUsersTotal = filteredUsers.length
    if (this.userSearch.length) {
      const regex = new RegExp(this.userSearch, 'i')
      filteredUsers = filteredUsers.filter(it => regex.test(it.name))
    }

    const end = this.pageRequestOfflineUsers.page * this.pageRequestOfflineUsers.size + this.pageRequestOfflineUsers.size
    this.offlineUsers.content = filteredUsers.slice(0, end)
    this.offlineUsers.totalElements = filteredUsers.length
  }

  /**
   * Create the current page for the inactive users list
   */
  loadInactivelineUsersPage (init? : boolean) {
    if (init) {
      this.pageRequestInactiveUsers = new PageRequest(this.ITEMS_PAGE)
      this.inactiveUsers = new PageResponse<any>()
    }

    let filteredUsers = this.allUsers.filter(it => it.status === TrackingUserStatus.INACTIVE)
    this.inactiveUsersTotal = filteredUsers.length
    if (this.userSearch.length) {
      const regex = new RegExp(this.userSearch, 'i')
      filteredUsers = filteredUsers.filter(it => regex.test(it.name))
    }

    const end = this.pageRequestInactiveUsers.page * this.pageRequestInactiveUsers.size + this.pageRequestInactiveUsers.size
    this.inactiveUsers.content = filteredUsers.slice(0, end)
    this.inactiveUsers.totalElements = filteredUsers.length
  }

  /**
   * Go to the next page of a user list by the status
   */
  showMore (status: number) {
    switch (status) {
      case TrackingUserStatus.ONLINE:
        this.pageRequestOnlineUsers.page++
        this.loadOnlineUsersPage()
        break
      case TrackingUserStatus.OFFLINE:
        this.pageRequestOfflineUsers.page++
        this.loadOfflineUsersPage()
        break
      case TrackingUserStatus.INACTIVE:
        this.pageRequestInactiveUsers.page++
        this.loadInactivelineUsersPage()
        break
    }
  }

  /**
   * Show / Hide users by their status
   */
  toggleUsersByStatus (status: number) {
    switch (status) {
      case TrackingUserStatus.ONLINE:
        this.usersOnlineDiabled = !this.usersOnlineDiabled
        break
      case TrackingUserStatus.OFFLINE:
        this.usersOfflineDiabled = !this.usersOfflineDiabled
        break
      case TrackingUserStatus.INACTIVE:
        this.usersInactiveDiabled = !this.usersInactiveDiabled
        break
    }

    this.cleanSelectedUser()
    this.initUsersByStatus(true)
  }

  /**
   * Active the status online list
   */
  activeAllStatus () {
    this.usersOnlineDiabled = false
    this.initUsersByStatus(true)
  }

  /**
   * Perform the user search
   */
  findUsersBySearch () {
    if (this.loadingSearch || !this.userSearch.length) return
    this.cleanSelectedUser()
    this.loadingSearch = true
    this.loadOnlineUsersPage(true)
    this.loadOfflineUsersPage(true)
    this.loadInactivelineUsersPage(true)
    this.searchInputText.blur()

    setTimeout(() => {
      this.searchPerformed = true
      this.loadingSearch = false
    }, 500)
  }

  /**
   * Clear user input search
   */
  clearUserSearch () {
    this.userSearch = ''
    this.searchPerformed = false
    this.initUsersByStatus(true)
  }

  /**
   * Init all users lists
   */
  initUsersByStatus (init?: boolean) {
    this.checkStatusToShow()
    this.loadOnlineUsersPage(init)
    this.loadOfflineUsersPage(init)
    this.loadInactivelineUsersPage(init)
  }

  /**
   * Determines the page sizes based on the status to show
   */
  checkStatusToShow () {
    let count = 0
    count += this.usersOnlineDiabled ? 0 : 1
    count += this.usersOfflineDiabled ? 0 : 1
    count += this.usersInactiveDiabled ? 0 : 1
    switch (count) {
      case 1:
        this.ITEMS_PAGE = 11
        break
      case 2:
        this.ITEMS_PAGE = 5
        break
      case 3:
        this.ITEMS_PAGE = 4
        break
    }
  }

  getUserFullName (user: TrackingUser) {
    return trackingService.getUserFullName(user)
  }

  getUserGroupsNames (user: TrackingUser) {
    return trackingService.getUserGroupsNames(user)
  }

  /**
   * Focus on the selected user in the map
   */
  zoomInSelectedUser () {
    this.currentMapView = this.MAP_VIEW.USERS
    this.tasksVisibleOnMap = false
    this.pathVisibleOnMap = false
    this.resetUserTasks()
    this.$root.$emit('EVENT_TRACKING_ZOOM_USER')
  }

  /**
   * Toggle the button to perform auto zoom feature
   */
  toggleUsersAutoZoom () {
    this.usersAutoZoom = !this.usersAutoZoom
  }

  /**
   * Open connection report in a new window
   */
  openConnectionReport () {
    const date = moment().format('DDMMYYYY')
    let routeData = this.$router.resolve({ name: 'report_cx', query: { template: this.templateSelected.toString(), initDate: date, endDate: date, group: this.selectedGroup.id.toString() } })
    window.open(routeData.href, '_blank')
  }

  /************************************************************************************************/
  /* USER TASKS ***********************************************************************************/
  /************************************************************************************************/

  /**
   * Focuses the template selector
   */
  focusTemplateSelector () {
    let templateSelector = document.getElementById('template-selector-wrapper')
    templateSelector!.focus()
  }

  /**
   * Event on user task button click
   */
  toggleUserTasks () {
    if (!this.templateSelected) {
      this.missingTemplate = true
      this.focusTemplateSelector()
      return
    }

    if (this.tasksVisibleOnMap) {
      this.$root.$emit('EVENT_TRACKING_TASKS_RESET')
      this.resetUserTasks()
      this.tasksVisibleOnMap = false
      this.tasksPathPriority = 'path'
    } else {
      this.tasksPathPriority = 'tasks'
      this.currentMapView = this.MAP_VIEW.TASKS_AND_PATH
      this.tasksVisibleOnMap = true
      this.loadUserTasks()
    }
  }

  /**
   * Gets the worked user tasks from the api
   */
  async loadUserTasks () {
    try {
      this.loadingUserTasksWorked = true
      let filters = new TrackingUserTasksFilters()
      filters.templateId = this.templateSelected
      filters.userId = this.userSelected.id
      filters.user = this.userSelected
      filters.initDate = this.dateSelected
      filters.endDate = this.dateSelected
      filters.worked = true
      filters.locationStatus = [
        LocationStatusFills.IN_SITE,
        LocationStatusFills.OUT_OF_SITE
      ]
      this.cursorRequestUserTasksWorked.filters = filters
      let cursorResponse = await trackingService
        .getPagedUserTasksAPIFILLS(
          this.cursorRequestUserTasksWorked,
          this.templateIdents
        )
      this.cursorRequestUserTasksWorked.cursor = cursorResponse.nextCursor // assign the next cursor

      this.userTasksWorked = cursorResponse.results
      this.totalUserTasksWorked = cursorResponse.total
      this.initTasksByStatus()
    } finally {
      this.loadingUserTasksWorked = false
    }
  }

  /**
   * Gets the pending user tasks from the api
   */
  async loadUserTasksPending () {
    try {
      this.loadingUserTasksPending = true
      let filters = new TrackingUserTasksFilters()
      filters.templateId = this.templateSelected
      filters.userId = this.userSelected.id
      filters.user = this.userSelected
      filters.initDate = new Date()
      filters.initDate.setDate(filters.initDate.getDate() - 31) // we consider a 31 day range
      filters.endDate = new Date()
      filters.worked = false
      filters.altUser = this.userSelected
      filters.locationStatus = [
        LocationStatusFills.NOT_WORKED
      ]
      this.cursorRequestUserTasksPending.filters = filters
      let cursorResponse = await trackingService
        .getPagedUserTasksAPIFILLS(
          this.cursorRequestUserTasksPending,
          this.templateIdents
        )
      this.cursorRequestUserTasksPending.cursor = cursorResponse.nextCursor // assign the next cursor
      this.userTasksPending = this.userTasksPending.concat(cursorResponse.results)
      this.totalUserTasksPending = cursorResponse.total
      this.initTasksByStatus()
    } catch (error) {
      LogService.error(error)
    } finally {
      setTimeout(() => {
        this.loadingUserTasksPending = false
      }, 500)
    }
  }

  /**
   * Load the next page of pending tasks
   */
  showMorePendingUserTasks () {
    this.loadUserTasksPending()
  }

  /**
   * Clean user tasks data
   */
  resetUserTasks () {
    this.uncompletedTasksDiabled = true
    this.cursorRequestUserTasksWorked = new CursorRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE_FILLS, true)
    this.cursorRequestUserTasksPending = new CursorRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE_FILLS, true)
    this.userTasks = []
    this.userTasksWorked = []
    this.userTasksPending = []
    this.completedTasks = []
    this.uncompletedTasks = []
    this.completedWarnTasks = []
    this.completedTasksValid = []
    this.uncompletedTasksValid = []
    this.completedWarnTasksValid = []
    this.totalUserTasksWorked = 0
    this.totalUserTasksPending = 0
  }

  /**
   * Clean PENDING user tasks
   */
  resetUserTasksPending () {
    this.cursorRequestUserTasksPending = new CursorRequest<TrackingUserTasksFilters>(this.MAX_TASKS_PAGE_SIZE_FILLS, true)
    this.userTasksPending = []
    this.totalUserTasksPending = 0
  }

  /**
   * Event fired when a task is clicked in the map
   */
  selectTaskFromMap (taskId: number) {
    const task = this.userTasks.find(it => it.id === taskId)
    this.taskSelected = task
  }

  cleanSelectedTask () {
    this.taskSelected = null
  }

  /**
   * Show / Hide tasks based in their status
   */
  toggleTasksByStatus (status: number) {
    switch (status) {
      case TrackingUserTaskLocationStatus.COMPLETE:
        this.completedTasksDiabled = !this.completedTasksDiabled
        break
      case TrackingUserTaskLocationStatus.COMPLETE_WARN:
        this.completedWarnTasksDiabled = !this.completedWarnTasksDiabled
        break
      case TrackingUserTaskLocationStatus.UNCOMPLETE:
        if (this.loadingUserTasksPending) break
        this.uncompletedTasksDiabled = !this.uncompletedTasksDiabled
        if (this.uncompletedTasksDiabled) {
          this.resetUserTasksPending()
        } else {
          this.loadUserTasksPending()
        }
        break
    }

    this.initTasksByStatus()
  }

  /**
   * Init the tasks and create the list for each status
   */
  initTasksByStatus () {
    this.userTasks = this.userTasksWorked.concat(this.userTasksPending)

    this.completedTasks = this.userTasks.filter(it => it.status === TrackingUserTaskLocationStatus.COMPLETE)
    this.completedWarnTasks = this.userTasks.filter(it => it.status === TrackingUserTaskLocationStatus.COMPLETE_WARN)
    this.uncompletedTasks = this.userTasks.filter(it => it.status === TrackingUserTaskLocationStatus.UNCOMPLETE)

    this.completedTasksValid = trackingService.filterValidTasks(this.completedTasks)
    this.completedWarnTasksValid = trackingService.filterValidTasks(this.completedWarnTasks)
    this.uncompletedTasksValid = trackingService.filterValidTasks(this.uncompletedTasks)
  }

  /************************************************************************************************/
  /* USER PATH ************************************************************************************/
  /************************************************************************************************/

  /**
   * Event on user path button click
   */
  toggleUserPath () {
    if (this.pathVisibleOnMap) {
      this.pathVisibleOnMap = false
      this.tasksPathPriority = 'tasks'
      this.hidePathCount()
    } else {
      this.pathVisibleOnMap = true
      this.tasksPathPriority = 'path'
      this.currentMapView = this.MAP_VIEW.TASKS_AND_PATH
      if (this.pathUserId !== this.userSelected.id) {
        this.loadUserPath()
      }
    }
  }

  /**
   * Toggle the button to show the snapped path
   */
  toggleSnappedPath () {
    if (this.pathCount) {
      this.snappedPathActive = !this.snappedPathActive
    }
  }

  /**
   * Toggle to show and calculate path kilometer count
   */
  togglePathCount () {
    this.$root.$emit('EVENT_TRACKING_TOGGLE_PATH_COUNT', this.pathCountVisible)
    this.pathCountVisible = !this.pathCountVisible
  }

  /**
   * Hide path count, used when user, template or path change
   */
  hidePathCount () {
    this.pathCountVisible = false
    this.$root.$emit('EVENT_TRACKING_TOGGLE_PATH_COUNT', true)
  }

  /**
   * Calculate total distance and show and fix decimal values
   */
  setPathCountValues (values: any[]) {
    this.totalKilometers = 0
    this.kilometersEstimated = false
    this.pathCountLabels = values.map(({ value, color, isEstimate }) => {
      value /= 1000
      this.totalKilometers += value
      this.kilometersEstimated = this.kilometersEstimated || isEstimate
      return {
        value: value.toFixed(1),
        color,
        isEstimate
      }
    })
    this.loadingPathCount = false
  }

  /**
   * Gets the user path from firebase
   */
  async loadUserPath () {
    try {
      this.loadingPath = true

      this.hidePathCount()
      this.$root.$emit('EVENT_TRACKING_USER_PATH_RESET')
      this.pathCount = 0
      this.pathUserId = this.userSelected.id
      this.firstUserConnection = ''
      this.lastUserConnection = ''
      let count = 0

      let initUserPathDate = moment(this.dateSelected).startOf('day').utc()
      let endUserPathDate = moment(this.dateSelected).endOf('day').utc()

      const domainUID = cookiesService.get(config.domainUIDCookieName)

      // Day 1
      const userPathRefDay1 = firebaseService.getUserPositionsRef(domainUID, this.userSelected.id, initUserPathDate).orderByKey().startAt(initUserPathDate.valueOf().toString())
      const snapshotDay1 = await userPathRefDay1.once('value')
      if (snapshotDay1.val()) {
        LogService.log(snapshotDay1.val())
        const itemsDay1: Array<any> = Object.values(snapshotDay1.val())
        const firstItem = trackingPositionsVersionsHandler.getValidPositionObject(itemsDay1[0])
        await this.loadUserSessions(firstItem)
        itemsDay1.forEach((it: any) => {
          this.processUserPath(it)
          count++
        })
      }

      // Day 2 (UTS Cross day)
      const userPathRefDay2 = firebaseService.getUserPositionsRef(domainUID, this.userSelected.id, endUserPathDate).orderByKey().endAt(endUserPathDate.valueOf().toString())
      const snapshotDay2 = await userPathRefDay2.once('value')
      if (snapshotDay2.val()) {
        LogService.log(snapshotDay2.val())
        const itemsDay2 = Object.values(snapshotDay2.val())
        itemsDay2.forEach((it: any) => {
          this.processUserPath(it)
          count++
        })
      }

      this.pathCount = count
    } catch (error) {
      LogService.error(error)
    } finally {
      this.loadingPath = false
    }
  }

  processUserPath (userPathItem: any) {
    try {
      let val = trackingPositionsVersionsHandler.getValidPositionObject(userPathItem)

      if (!this.positionTrackedWithOpenSession(val)) {
        return
      }

      if (val.time) {
        if (!this.firstUserConnection) {
          this.firstUserConnection = val.time
        }
        this.lastUserConnection = val.time
      }
      this.$root.$emit('EVENT_TRACKING_ADD_USER_PATH', val)
    } catch (error) {
      LogService.error(error)
    }
  }

  async loadUserSessions (oldestPosition: TrackingUserPosition) {
    const positionTimestamp = oldestPosition._timestamp.valueOf()
    let pageUserSessionRequest = new PageRequest<PageRequestTrackingUserSession>()
    pageUserSessionRequest.sort = 'acquiredDate'
    pageUserSessionRequest.sortOrder = SortDirection.DESC
    pageUserSessionRequest.offset = 0
    pageUserSessionRequest.filters = { user: this.userSelected.id }
    if (!this.userSessionsLoaded) {
      this.userSessions = await trackingService.getUserSessions(pageUserSessionRequest)
      this.userSessionsLoaded = true
    }

    let oldestSession = this.userSessions.results[this.userSessions.size - 1].acquiredDate
    while (positionTimestamp < oldestSession && this.userSessions.size < this.userSessions.total) {
      pageUserSessionRequest.offset = this.userSessions.size
      const sessionsResponse = await trackingService.getUserSessions(pageUserSessionRequest)
      oldestSession = sessionsResponse.results[sessionsResponse.size - 1].acquiredDate
      this.userSessions.size += sessionsResponse.size
      this.userSessions.results = this.userSessions.results.concat(sessionsResponse.results)
    }
  }

  /************************************************************************************************/
  /* VIEW UTILS ***********************************************************************************/
  /************************************************************************************************/

  /**
   * Selector date delimiter
   */
  notBeforeDate (date: Date) {
    return date < this.DATE_31_DAYS_BEFORE
  }

  /**
   * Get the corresponding text for precision title
   */
  getPrecisionText (confidence: number) {
    return `${confidence}% de probabilidad`
  }

  /*
   * Evaluate if a TrackingPosition was collected while the user was logged in
   */
  positionTrackedWithOpenSession (val: TrackingUserPosition) {
    const positionTime = val._timestamp.valueOf() + 60 * 1000
    const arr = this.userSessions.results

    // Do binary search to evaluate if position was recorded between
    // 2 open session
    let left = 0
    let right = arr.length - 1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      if (mid === 0) {
        return this.checkAcquiredReleasedDate(positionTime, arr[mid])
      } else if (arr[mid].acquiredDate <= positionTime && positionTime < arr[mid - 1].acquiredDate) {
        return this.checkAcquiredReleasedDate(positionTime, arr[mid])
      } else if (arr[mid].acquiredDate > positionTime) {
        left = mid + 1
      } else {
        right = mid - 1
      }
    }
    return false
  }

  checkAcquiredReleasedDate (time: number, session: TrackingUserSession) {
    if ((session.active && session.acquiredDate <= time) ||
        (session.acquiredDate <= time && time <= Number(session.releaseDate))) {
      return true
    }
    return false
  }
}
